'use client'

import { useRef, useState } from 'react'
import { useRouter } from 'next/navigation'
import { trackEvent } from '@/lib/analytics'
import { IDENTIFY_CTA } from '@/lib/identify-cta'
import { DIAGNOSE_CTA } from '@/lib/blog-diagnose-cta'
import { stashPendingScanFile } from '@/lib/pending-scan-file'

// iOS sometimes reports an empty MIME type from the picker. Infer from the
// extension instead of guessing; an empty type is fine downstream -- the
// homepage upload handler already falls back to extension matching.
const EXT_MIME: Record<string, string> = {
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  png: 'image/png',
  webp: 'image/webp',
  heic: 'image/heic',
  heif: 'image/heif',
}

function inferMimeType(file: File): string {
  if (file.type) return file.type
  const ext = file.name.toLowerCase().split('.').pop() ?? ''
  return EXT_MIME[ext] ?? ''
}

/** File types the homepage analyzer accepts; use on the picker's `accept`. */
export const SCAN_ACCEPT = 'image/jpeg,image/png,image/webp,image/heic,image/heif'

/**
 * Shared "pick a grass photo here, analyze it on the homepage" handoff.
 *
 * The picked file is stashed in module state (lib/pending-scan-file.ts) and
 * consumed by the homepage flow on arrival; the stash survives because App
 * Router navigates client-side. The iOS-safe byte materialization below is
 * subtle enough that it should stay in exactly one place.
 *
 * Rendered by components/blog/inline-scan-cta.tsx (mid-article, the post-read
 * peak, AND the blog diagnose picker V2). It lives here rather than inline
 * because callers render their own trigger and their own <input>, wiring
 * `inputRef` and `onInputChange`, so each surface keeps its own layout and
 * analytics location without re-implementing the handoff.
 *
 * `destination` is where the stashed photo rides to: the homepage grass-ID flow
 * ('/#analysis-card', default) or '/diagnose' for the diagnose picker. The
 * consuming effect (use-homepage-analysis) mounts on both routes and picks the
 * stash up on either.
 */
export function useScanHandoff(ctaLocation: string, destination: string = '/#analysis-card') {
  const router = useRouter()
  const inputRef = useRef<HTMLInputElement>(null)
  const [handingOff, setHandingOff] = useState(false)
  const targetsDiagnose = destination.startsWith(DIAGNOSE_CTA.href)

  const openPicker = () => inputRef.current?.click()

  const onFileSelected = async (file: File) => {
    setHandingOff(true)
    try {
      // Materialize the bytes NOW: iOS Safari can revoke picker File references
      // after navigation, so a lazy read on the homepage would intermittently fail.
      const bytes = await file.arrayBuffer()
      const stable = new File([bytes], file.name || 'grass-photo.jpg', {
        type: inferMimeType(file),
      })
      stashPendingScanFile(stable, ctaLocation)
      trackEvent('blog_scan_handoff', {
        cta_location: ctaLocation,
        status: 'file_selected',
        file_size_kb: Math.round(stable.size / 1024),
      })
      router.push(destination)
    } catch {
      // Unreadable file (permissions, transient iOS failure): fall back to the
      // plain navigation the old banner performed, to whichever flow the caller
      // targets (diagnose vs grass-ID).
      setHandingOff(false)
      trackEvent('blog_scan_handoff', {
        cta_location: ctaLocation,
        status: 'file_read_failed',
      })
      router.push(targetsDiagnose ? DIAGNOSE_CTA.href : IDENTIFY_CTA.href)
    }
  }

  const onInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    // Reset so re-picking the same file re-fires change.
    e.target.value = ''
    if (file) void onFileSelected(file)
  }

  return { inputRef, handingOff, openPicker, onInputChange }
}
